import type { PlaceSummary } from './types';

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export const today = () => new Date().getDay();

/** true / false, or null when hours are unknown. */
export function openOn(p: Pick<PlaceSummary, 'weeklyHours'>, day: number): boolean | null {
  if (!p.weeklyHours || p.weeklyHours.length !== 7) return null;
  return p.weeklyHours[day] !== null;
}

export function hoursText(p: Pick<PlaceSummary, 'weeklyHours'>, day: number): string {
  const open = openOn(p, day);
  if (open === null) return 'Hours unknown, call ahead';
  return open ? `${DAYS[day]} ${p.weeklyHours![day]}` : `Closed ${DAYS_LONG[day]}s`;
}

export function place(p: Pick<PlaceSummary, 'town' | 'state'>): string {
  return [p.town, p.state].filter(Boolean).join(', ');
}

export function minutes(total: number): string {
  const m = Math.round(total);
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h ? `${h}h ${r}m` : `${r}m`;
}

export function miles(n: number): string {
  return n < 10 ? `${n.toFixed(1)} mi` : `${Math.round(n).toLocaleString()} mi`;
}

export function scoreLabel(score: number): string {
  if (score >= 80) return 'True backroad';
  if (score >= 65) return 'Local haunt';
  if (score >= 50) return 'Small-town spot';
  return 'Town spot';
}

/** Rough drive estimate between two points (backroads ≈ 1.35× straight line at ~42 mph). */
export function estimateLeg(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 3958.8;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  const straight = 2 * R * Math.asin(Math.sqrt(h));
  if (straight < 0.6) return { miles: straight * 1.2, minutes: Math.max(2, Math.round(((straight * 1.2) / 3) * 60)), walk: true };
  const mi = straight * 1.35;
  return { miles: mi, minutes: Math.round((mi / 42) * 60), walk: false };
}

export function directionsUrl(p: { lat: number; lng: number }): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`;
}

// Hours come as "7a–9p", "12p–2a" or "11a–2p, 5p–12a" (en dashes), one string per day.
function parseClock(token: string, fallbackSuffix?: string): number | null {
  const m = /(\d{1,2})(?::(\d{2}))?\s*([ap])?/i.exec(token.trim());
  if (!m) return null;
  const suffix = (m[3] ?? fallbackSuffix ?? '').toLowerCase();
  let h = Number(m[1]) % 12;
  if (suffix === 'p') h += 12;
  if (!suffix && Number(m[1]) === 12) h = 12;
  return h * 60 + Number(m[2] ?? 0);
}

function clockText(token: string): string {
  const t = token.trim().toLowerCase();
  if (t === '12a') return 'midnight';
  if (t === '12p') return 'noon';
  return t.replace(/([ap])$/, ' $1m');
}

/** "Open until 2 am", "Opens 11 am", "Closed today": for the next-stop card. */
export function openStatus(p: Pick<PlaceSummary, 'weeklyHours'>, now = new Date()): { text: string; open: boolean | null } {
  const day = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const hours = p.weeklyHours;
  if (!hours || hours.length !== 7) return { text: 'Hours unknown, call ahead', open: null };

  const span = (d: number) => {
    const text = hours[d];
    if (!text) return null;
    const ranges = text.split(',').map((r) => r.split(/[-–]/));
    const first = ranges[0];
    const last = ranges[ranges.length - 1];
    if (first.length < 2 || last.length < 2) return null;
    const endSuffix = /([ap])\s*$/i.exec(first[1])?.[1];
    const start = parseClock(first[0], endSuffix);
    let end = parseClock(last[1]);
    if (start === null || end === null) return null;
    if (end <= start) end += 24 * 60; // closes after midnight
    return { start, end, startText: clockText(first[0]), endText: clockText(last[1]) };
  };

  // Just after midnight the night before may still be going.
  const yesterday = span((day + 6) % 7);
  if (yesterday && mins + 24 * 60 < yesterday.end) return { text: `Open until ${yesterday.endText}`, open: true };

  const todays = span(day);
  if (!hours[day]) return { text: `Closed ${DAYS_LONG[day]}s`, open: false };
  if (!todays) return { text: `${DAYS[day]} ${hours[day]}`, open: null };
  if (mins < todays.start) return { text: `Opens ${todays.startText}, until ${todays.endText}`, open: false };
  if (mins < todays.end) return { text: `Open until ${todays.endText}`, open: true };
  return { text: 'Closed for the night', open: false };
}
